import type { UseQueryOptions } from "@tanstack/react-query";
import { queryOptions, skipToken, useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { apiWithoutCamelization } from "../api";
import type {
  ShowErrorToastFn,
  ShowToastFn
} from "../mantine/notifications/useToast";
import urls from "../urls";
import type {
  SiteCategoriesResponse,
  SiteCategoriesSummaryResponse,
  SiteCategoriesUpdateRequest
} from "../utils/backend-types";
import { getSiteCategoriesUnion } from "../utils/getSiteCategoriesUnion";

export async function patchSiteCategories(
  showToast: ShowToastFn,
  showErrorToast: ShowErrorToastFn,
  projectId: string,
  siteCategories: Array<SiteCategoriesUpdateRequest> | undefined
) {
  await apiWithoutCamelization
    .patch(
      urls.apiWithoutCamelization.siteCategories(projectId),
      siteCategories
    )
    .then(() => {
      showToast({
        type: "success",
        message: "Die Liegenschafts-Kategorien wurden erfolgreich aktualisiert."
      });
    })
    .catch((error) => {
      showErrorToast(error);
    });
}

type SiteCategoriesOutput = {
  siteCategories: Array<SiteCategoriesResponse> | undefined;
  siteCategoriesSummary: SiteCategoriesSummaryResponse | undefined;
  isLoading: boolean;
  error: Error | null;
};

export const siteCategoriesQueries = {
  all: () =>
    queryOptions({
      queryKey: ["siteCategories"]
    }),
  detail: (projectOrSiteId?: string | number | null) =>
    queryOptions<Array<SiteCategoriesResponse>>({
      queryKey: ["siteCategories", { projectOrSiteId }],
      queryFn: projectOrSiteId
        ? () => fetchSiteCategories(projectOrSiteId)
        : skipToken,
      staleTime: 5 * 60 * 1000 // 5 minutes)
    })
};

export function useSiteCategories(
  projectOrSiteId?: string | number | null,
  options?: Partial<UseQueryOptions<Array<SiteCategoriesResponse>>>
): SiteCategoriesOutput {
  const {
    data: siteCategories,
    isLoading,
    error
  } = useQuery({
    ...siteCategoriesQueries.detail(projectOrSiteId),
    ...options
  });

  const siteCategoriesSummary = useMemo(
    () => getSiteCategoriesUnion(siteCategories),
    [siteCategories]
  );

  return { siteCategories, siteCategoriesSummary, isLoading, error };
}

async function fetchSiteCategories(projectOrSiteId: string | number) {
  const siteCategoriesUrl =
    typeof projectOrSiteId === "number"
      ? urls.apiWithoutCamelization.siteCategoryForSite(projectOrSiteId)
      : urls.apiWithoutCamelization.siteCategories(projectOrSiteId);

  const response =
    await apiWithoutCamelization.get<Array<SiteCategoriesResponse>>(
      siteCategoriesUrl
    );
  return response.data;
}
